import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import {
  Bold, Italic, Underline, Link as LinkIcon, Image as ImageIcon,
  Code2, Eye, RotateCcw, Save as SaveIcon, Type,
} from 'lucide-react';

/**
 * Lightweight WYSIWYG editor for the SMTP email signature.
 *
 * Two modes:
 *  - Visual (contentEditable + toolbar)
 *  - HTML (raw textarea)
 * Toggle between them with the "HTML" / "Visual" button.
 *
 * Props:
 *   value         (string)   current signature_html
 *   onChange      (fn)       called on every edit with new html string
 *   onSave        (fn)       called when the Save button is pressed
 *   saving        (bool)     show spinner state on the save button
 *   logoUrl       (string)   optional preview-only logo
 *   senderName    (string)   to seed a compliant footer
 *   senderEmail   (string)
 */
const ACCENT = '#e6007e';

// --------- Signature presets (polished, email-client safe) ---------
//
// All presets are built from inline-styled <p> elements only — no <table>, no <ul>.
// Apple Mail / iOS / Outlook all respect inline styles on <p>, so the design
// renders identically across clients. Avoid bullet lists in signatures (they
// look unprofessional in most clients).
const PRESET_MODERN = (name, email) => {
  const safeName = (name || 'Your Name').trim();
  const [first, ...rest] = safeName.split(' ');
  const role = rest.length ? rest.join(' ') : '';
  const dom = (email || 'you@yourdomain.com').split('@')[1] || 'yourdomain.com';
  return `
<p style="margin:0 0 6px 0;color:#374151;font-size:14px;">Best regards,</p>
<p style="margin:0;font-weight:600;font-size:16px;color:#111827;letter-spacing:-0.01em;">${safeName}</p>
${role ? `<p style="margin:2px 0 0 0;color:#6b7280;font-size:13px;">${role}</p>` : ''}
<p style="margin:10px 0 0 0;font-size:13px;color:#374151;">
  <a href="mailto:${email || ''}" style="color:#e6007e;text-decoration:none;font-weight:500;">${email || 'you@yourdomain.com'}</a>
  <span style="color:#d1d5db;margin:0 8px;">|</span>
  <a href="https://${dom}" style="color:#6b7280;text-decoration:none;">${dom}</a>
</p>
<p style="margin:14px 0 0 0;font-size:11px;color:#9ca3af;line-height:1.5;">
  Paify Ltd · 123 Example Street, London, UK ·
  <a href="https://paify.io/privacy" style="color:#9ca3af;text-decoration:underline;">Privacy</a>
</p>
`.trim();
};

const PRESET_CLASSIC = (name, email) => {
  const safeName = (name || 'Your Name').trim();
  return `
<p style="margin:0 0 4px 0;color:#1f2937;font-size:14px;">Kind regards,</p>
<p style="margin:0 0 2px 0;font-weight:600;font-size:15px;color:#111827;">${safeName}</p>
<p style="margin:0 0 8px 0;color:#4b5563;font-size:13px;font-style:italic;">Paify</p>
<p style="margin:0;font-size:13px;color:#4b5563;">
  <a href="mailto:${email || ''}" style="color:#e6007e;text-decoration:none;">${email || 'you@yourdomain.com'}</a>
</p>
<p style="margin:12px 0 0 0;font-size:11px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:8px;">
  Paify Ltd · 123 Example Street, London, UK ·
  <a href="https://paify.io/privacy" style="color:#9ca3af;">Privacy</a>
</p>
`.trim();
};

const PRESET_MINIMAL = (name, email) => {
  const safeName = (name || 'Your Name').trim();
  return `
<p style="margin:0;color:#111827;font-size:14px;">— ${safeName}</p>
<p style="margin:4px 0 0 0;font-size:12px;color:#9ca3af;">
  <a href="mailto:${email || ''}" style="color:#9ca3af;text-decoration:none;">${email || 'you@yourdomain.com'}</a>
</p>
<p style="margin:10px 0 0 0;font-size:11px;color:#9ca3af;">
  Paify Ltd, London, UK · <a href="https://paify.io/privacy" style="color:#9ca3af;">Privacy</a>
</p>
`.trim();
};

const PRESETS = {
  modern: { label: 'Modern', build: PRESET_MODERN },
  classic: { label: 'Classic', build: PRESET_CLASSIC },
  minimal: { label: 'Minimal', build: PRESET_MINIMAL },
};

export default function SignatureEditor({
  value = '',
  onChange,
  onSave,
  saving = false,
  logoUrl = '',
  senderName = '',
  senderEmail = '',
}) {
  const editorRef = useRef(null);
  const [mode, setMode] = useState('visual'); // 'visual' | 'html'
  const [showPreview, setShowPreview] = useState(true);

  // Keep contentEditable HTML in sync with `value` when switching modes / external updates,
  // but avoid clobbering the user's caret while they are typing.
  useEffect(() => {
    if (mode === 'visual' && editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value, mode]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    if (editorRef.current) onChange?.(editorRef.current.innerHTML);
  };

  const insertLink = () => {
    const url = window.prompt('Enter URL (e.g. https://paify.io)');
    if (!url) return;
    exec('createLink', url);
  };

  const insertImage = () => {
    const url = window.prompt('Image URL');
    if (!url) return;
    exec('insertImage', url);
  };

  const handleVisualInput = () => {
    if (editorRef.current) onChange?.(editorRef.current.innerHTML);
  };

  const insertPreset = (key) => {
    const preset = PRESETS[key];
    if (!preset) return;
    const next = preset.build(senderName, senderEmail);
    onChange?.(next);
    if (editorRef.current && mode === 'visual') editorRef.current.innerHTML = next;
  };

  const ToolbarBtn = ({ onClick, title, children, testid }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="px-2 py-1.5 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
    >
      {children}
    </button>
  );

  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden bg-gray-900" data-testid="signature-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 bg-gray-800 border-b border-gray-700">
        {mode === 'visual' && (
          <>
            <ToolbarBtn onClick={() => exec('bold')} title="Bold" testid="sig-bold"><Bold className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => exec('italic')} title="Italic" testid="sig-italic"><Italic className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => exec('underline')} title="Underline" testid="sig-underline"><Underline className="w-4 h-4" /></ToolbarBtn>
            <span className="w-px h-5 bg-gray-700 mx-1" />
            <ToolbarBtn onClick={insertLink} title="Insert link" testid="sig-link"><LinkIcon className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={insertImage} title="Insert image" testid="sig-image"><ImageIcon className="w-4 h-4" /></ToolbarBtn>
            <span className="w-px h-5 bg-gray-700 mx-1" />
            <ToolbarBtn onClick={() => exec('formatBlock', 'p')} title="Paragraph" testid="sig-para"><Type className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => exec('removeFormat')} title="Clear formatting" testid="sig-clear"><RotateCcw className="w-4 h-4" /></ToolbarBtn>
          </>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            data-testid="sig-toggle-preview"
            className="px-2 py-1.5 text-xs text-gray-300 hover:text-white hover:bg-gray-700 rounded inline-flex items-center gap-1"
            title="Toggle preview"
          >
            <Eye className="w-3.5 h-3.5" />{showPreview ? 'Hide preview' : 'Preview'}
          </button>
          <button
            type="button"
            onClick={() => setMode((m) => (m === 'visual' ? 'html' : 'visual'))}
            data-testid="sig-toggle-mode"
            className="px-2 py-1.5 text-xs text-gray-300 hover:text-white hover:bg-gray-700 rounded inline-flex items-center gap-1"
            title="Switch between visual and HTML"
          >
            <Code2 className="w-3.5 h-3.5" />{mode === 'visual' ? 'HTML' : 'Visual'}
          </button>
        </div>
      </div>

      {/* Editor surface */}
      {mode === 'visual' ? (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleVisualInput}
          onBlur={handleVisualInput}
          data-testid="sig-visual-area"
          className="min-h-[140px] p-3 bg-white text-gray-900 focus:outline-none [&_a]:text-blue-600 [&_a]:underline"
          style={{ fontFamily: 'Segoe UI, Helvetica, Arial, sans-serif', fontSize: 14 }}
        />
      ) : (
        <textarea
          value={value || ''}
          onChange={(e) => onChange?.(e.target.value)}
          spellCheck={false}
          data-testid="sig-html-area"
          className="w-full min-h-[180px] p-3 bg-gray-900 text-gray-100 font-mono text-xs focus:outline-none border-0 resize-y"
          placeholder='<p>Best regards,<br/>Your Name</p>'
        />
      )}

      {/* Footer: helpers + save */}
      <div className="px-3 py-2 bg-gray-800 border-t border-gray-700 flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-500 mr-1">Use a preset:</span>
        {Object.entries(PRESETS).map(([key, p]) => (
          <Button
            key={key}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => insertPreset(key)}
            className="border-gray-600 text-gray-200 hover:bg-gray-700 hover:text-white h-7 px-2 text-xs"
            data-testid={`sig-preset-${key}`}
          >
            {p.label}
          </Button>
        ))}
        <span className="text-[11px] text-gray-500 hidden md:inline ml-1">
          Tip: avoid bullet lists in signatures — they render as ugly dots in some clients.
        </span>
        <div className="ml-auto">
          <Button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="text-white h-8"
            style={{ background: ACCENT }}
            data-testid="sig-save-btn"
          >
            <SaveIcon className="w-4 h-4 mr-1.5" />{saving ? 'Saving…' : 'Save signature'}
          </Button>
        </div>
      </div>

      {/* Preview */}
      {showPreview && (
        <div className="border-t border-gray-700 bg-white" data-testid="sig-preview">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
            Live preview
          </div>
          <div className="p-4">
            <div
              className="text-gray-800"
              style={{ fontFamily: 'Segoe UI, Helvetica, Arial, sans-serif', fontSize: 14, lineHeight: 1.55 }}
              dangerouslySetInnerHTML={{ __html: value || '<em style="color:#999">Empty signature.</em>' }}
            />
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Logo"
                width="120"
                style={{ width: 120, maxWidth: 120, height: 'auto', marginTop: 14, display: 'block' }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
